import { useState, useEffect } from "react";
import axios from "axios";
import { Check, X, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VoiceAssignmentPanel = ({ episodeId, seriesFilter, onClose }) => {
  const [parsed, setParsed] = useState(null);
  const [assignments, setAssignments] = useState({});
  const [narrator, setNarrator] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [autoAssigning, setAutoAssigning] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [parseRes, assignRes, profileRes] = await Promise.all([
          axios.post(`${API}/episodes/${episodeId}/parse-dialogue`),
          axios.get(`${API}/episodes/${episodeId}/voice-assignments`),
          axios.get(`${API}/voice-profiles?series=${seriesFilter}`)
        ]);
        setParsed(parseRes.data);
        setAssignments(assignRes.data.character_voices || {});
        setNarrator(assignRes.data.narrator_voice_id || null);
        setProfiles(profileRes.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchData();
  }, [episodeId, seriesFilter]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/episodes/${episodeId}/voice-assignments`, {
        character_voices: assignments, narrator_voice_id: narrator
      });
      alert("Voice assignments saved!");
    } catch (e) { alert("Save failed"); }
    finally { setSaving(false); }
  };

  const handleAutoAssign = async () => {
    setAutoAssigning(true);
    try {
      const res = await axios.post(`${API}/episodes/${episodeId}/voice-assignments/auto`);
      setAssignments(res.data.character_voices || {});
      setNarrator(res.data.narrator_voice_id || null);
    } catch (e) { alert("Auto-assign failed"); }
    finally { setAutoAssigning(false); }
  };

  if (loading) return <div className="p-4 text-center"><div className="w-6 h-6 border-2 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto" /></div>;

  const characters = parsed?.characters || [];
  const segments = parsed?.segments || [];
  const typeColors = { dialogue: "border-blue-500/30 bg-blue-950/10", narration: "border-gray-500/20 bg-gray-900/20", sfx: "border-yellow-500/30 bg-yellow-950/10", stage_direction: "border-green-500/30 bg-green-950/10" };
  const typeLabels = { dialogue: "DIALOGUE", narration: "NARRATION", sfx: "SFX", stage_direction: "SCENE" };

  return (
    <div className="p-4 border border-purple-500/30 rounded-lg bg-purple-950/5" data-testid="voice-assignment-panel">
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-anton text-sm tracking-wider text-purple-400">VOICE ASSIGNMENTS</h4>
        <div className="flex gap-2">
          <button onClick={handleAutoAssign} disabled={autoAssigning}
            className="px-3 py-1.5 text-[11px] font-bold bg-purple-600/20 text-purple-400 border border-purple-500/20 rounded hover:bg-purple-600/30 transition-all"
            data-testid="auto-assign-btn">
            {autoAssigning ? "Matching..." : "Auto-Assign"}
          </button>
          <button onClick={handleSave} disabled={saving}
            className="px-3 py-1.5 text-[11px] font-bold bg-green-600 text-white rounded hover:bg-green-700 transition-all"
            data-testid="save-assignments-btn">
            {saving ? "Saving..." : "Save"}
          </button>
          <button onClick={onClose} className="px-2 py-1.5 text-gray-500 hover:text-white transition-all" data-testid="close-voice-assign">
            <X size={14} />
          </button>
        </div>
      </div>

      {/* Narrator Assignment */}
      <div className="mb-4 p-3 border border-amber-500/20 rounded bg-amber-950/10">
        <div className="flex items-center gap-3">
          <span className="text-xs font-bold text-amber-400 w-24 flex-shrink-0">NARRATOR</span>
          <select value={narrator || ""} onChange={e => setNarrator(e.target.value || null)}
            className="flex-1 p-1.5 text-xs bg-[#1a1a1a] border border-white/15 rounded text-white"
            data-testid="narrator-voice-select">
            <option value="">-- No narrator voice --</option>
            {profiles.filter(p => p.is_narrator).map(p => (
              <option key={p.id} value={p.id}>{p.character_name} ({p.ai_voice || "uploaded"})</option>
            ))}
            {profiles.filter(p => !p.is_narrator).map(p => (
              <option key={p.id} value={p.id}>{p.character_name} ({p.ai_voice || "uploaded"})</option>
            ))}
          </select>
        </div>
      </div>

      {/* Character Assignments */}
      {characters.length > 0 && (
        <div className="mb-4">
          <p className="text-xs text-gray-500 mb-2">{characters.length} characters detected</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {characters.map(char => (
              <div key={char} className="flex items-center gap-2 p-2 border border-white/5 rounded" data-testid={`char-voice-${char}`}>
                <span className="text-xs font-bold text-blue-400 truncate flex-1">{char}</span>
                <select value={assignments[char] || ""} onChange={e => setAssignments(prev => ({ ...prev, [char]: e.target.value || undefined }))}
                  className="w-40 p-1 text-[11px] bg-[#1a1a1a] border border-white/15 rounded text-white">
                  <option value="">-- Unassigned --</option>
                  {profiles.map(p => (
                    <option key={p.id} value={p.id}>{p.character_name} ({p.ai_voice || "up"})</option>
                  ))}
                </select>
                {assignments[char] && <Check size={12} className="text-green-500 flex-shrink-0" />}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Parsed Segments Preview */}
      <details className="mt-3">
        <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-300 mb-2">
          Preview {segments.length} segments
        </summary>
        <div className="space-y-1 max-h-[300px] overflow-y-auto">
          {segments.slice(0, 50).map((seg, i) => (
            <div key={i} className={`px-2 py-1.5 border rounded text-[11px] ${typeColors[seg.type] || "border-white/10"}`}>
              <span className="text-[9px] font-bold text-gray-500 mr-2">{typeLabels[seg.type]}</span>
              {seg.character && <span className="text-blue-400 font-bold mr-1">{seg.character}:</span>}
              <span className="text-gray-300">{seg.text.substring(0, 120)}{seg.text.length > 120 ? "..." : ""}</span>
            </div>
          ))}
          {segments.length > 50 && <p className="text-[10px] text-gray-600 text-center">+{segments.length - 50} more segments</p>}
        </div>
      </details>
    </div>
  );
};

export default VoiceAssignmentPanel;
